import React, { createContext, useRef, useState, useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { purchase as purchaseApi } from 'js-api-client';
import { getSessionId } from 'utils/session';
import { setCybersourceKeys } from '@/actions/config';
import { trackEvent } from 'user-analytics';
import { DIGITAL_CARD_RECOMMENDATION_SHOWED } from 'constants/TrackEvents';
import { CardBrands } from '../../utils/cards';
import { getBinFromCybersourceToken, getPreferredCybersourceCard } from '../../utils/Cybersource';

export const CybersourceMicroformFormContext = createContext();

// The Flex SDK script is injected without awaiting (initializers.js), so it may not be defined
// when the provider first tries to initialize. Poll for it on a short interval up to a bounded
// timeout instead of assuming it is present. Conservative timeout to tolerate slow devices/net;
// on timeout we surface the non-silent failure path rather than retrying forever.
const FLEX_SDK_POLL_INTERVAL_MS = 200;
const FLEX_SDK_READY_TIMEOUT_MS = 8000;
// Bounded wait for the capture-context JWT to arrive in the store. If it never does, surface the
// non-silent failure instead of a permanently blank form. Generous, since getPurchase normally
// resolves quickly; this only trips on a genuinely stuck/missing capture context.
const JWT_READY_TIMEOUT_MS = 12000;
// Bounded retry for a transient microform-build failure (flex ready but microform threw). Keeps
// partial-init recovery without relying on a render loop.
const MICROFORM_MAX_ATTEMPTS = 3;

/**
 * Disable Payment Request API to prevent Google Pay drawer in mobile
 */
function disablePaymentRequestAPI() {
  try {
    // Disable if we are in a mobile environment
    if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      Object.defineProperty(window, 'PaymentRequest', {
        configurable: true,
        get: () => undefined,
      });
      console.log('Payment Request API disabled to prevent Google Pay drawer');
    }
  } catch (err) {
    console.error('Failed to disable Payment Request API:', err);
  }
}

/**
 * Maps card brands to CyberSource type codes according to official documentation
 * @param {string} cardBrand - The card brand
 * @returns {string} The CyberSource type code
 */
const getCardTypeCode = (cardBrand) => {
  const cardTypeCodes = {
    [CardBrands.VISA]: '001', // Visa (includes Visa Electron)
    [CardBrands.ELECTRON]: '001', // Visa Electron - use 001 as per documentation
    [CardBrands.MASTERCARD]: '002', // Mastercard, Eurocard
    [CardBrands.AMEX]: '003', // American Express
    [CardBrands.DISCOVER]: '004', // Discover
    [CardBrands.DINERS]: '005', // Diners Club
    [CardBrands.JCB]: '007', // JCB
    [CardBrands.MAESTRO]: '042', // Maestro International
    [CardBrands.UNION_PAY]: '062', // China UnionPay
  };

  return cardTypeCodes[cardBrand]; // Return undefined if unknown, let CyberSource handle it
};

/**
 * Maps the CyberSource response to the expected format.
 * @param {string} token - The token
 * @param {Object} cardParams - The card parameters
 * @returns {Object} The mapped CyberSource response
 */
const mappedCyberResponse = (token, cardParams) => {
  return {
    credit_card_tokens: {
      cybersource_transient_token: token,
    },
    card_holder_name: cardParams.holderName,
    card_brand: cardParams.cardBrand,
  };
};

/**
 * Parse expiration date string in format MM/YY or MM/YYYY
 * @param {string} expirationDate - Expiration date in format MM/YY or MM/YYYY
 * @returns {Array} Array with [month, year]
 */
const parseExpirationDate = (expirationDate) => {
  const [month, year] = expirationDate.split('/');
  const fullYear = year.length === 2 ? `20${year}` : year;
  return [month, fullYear];
};

/**
 * CybersourceMicroformFormProvider component.
 * Provides context for the CybersourceMicroformForm component.
 *
 * @param {object} props - children.
 * @param {React.ReactNode} props.children - Child components to render.
 * @returns {React.ReactElement} Provider component.
 */
export const CybersourceMicroformFormProvider = ({ children }) => {
  // Deshabilitar la Payment Request API al cargar el componente
  useEffect(() => {
    disablePaymentRequestAPI();
  }, []);
  const [initialized, setInitialized] = useState({
    flex: false,
    microform: false,
    antifraudBeacon: false,
  });
  const [formValues, setFormValues] = useState({
    cardNumberValid: false,
    cardSecurityCodeValid: false,
    cardBrand: '',
    cybsCardType: '',
    holderName: '',
    expirationMonth: '',
    expirationYear: '',
    billingAddress: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [binMessageVisible, setBinMessageVisible] = useState(false);
  // Non-silent failure signal: set when initialization cannot complete (SDK never ready within
  // the timeout, or an unrecoverable init error). Consumers relay it to the WebView host.
  const [initError, setInitError] = useState(null);

  const dispatch = useDispatch();
  const { features, env } = useSelector((state) => state.whitelabelConfig);
  // Stable primitive selector for the CyberSource capture-context JWT. Reading the jwt string
  // directly (instead of usePurchase() -> state.purchase.toJS(), which returns a new object on
  // every render) keeps this value referentially stable, so findJWT/initializeCybersource and the
  // auto-init effect don't re-run on every render — this is what prevents the init render-storm
  // while the jwt is absent.
  const cybersourceJwt = useSelector((state) => {
    const methods = state.purchase.get('paymentMethods');
    const method = methods?.find((m) => m.get('engine') === 'cybersource');
    return method?.getIn(['extra', 'paymentGatewayToken', 'jwt']);
  });
  const { cybersourceLocalKeys } = useSelector((state) => state.config);
  const purchase = useSelector((state) => state.purchase);
  const purchaseToken = purchase?.get('token');
  const paymentCredentials = purchase?.get('paymentCredentials')?.toJS();
  const cybersourceKeys = paymentCredentials?.creditCard?.cybersource;

  const flex = useRef(null);
  const microform = useRef(null);
  const fields = useRef({});
  // The capture-context JWT the current Flex instance was built with. Used to detect when a new
  // purchase's context arrives so we can rebuild Flex against it instead of reusing a stale one.
  const currentCaptureContext = useRef(null);
  // Always reflects the most recent capture-context JWT, unlike the closures captured by an
  // in-flight initializeCybersource run (which awaits the SDK and can resolve after the JWT
  // changed). Used to abort a stale attempt before it commits Flex/microform against an old JWT.
  const liveJwtRef = useRef(cybersourceJwt);
  liveJwtRef.current = cybersourceJwt;
  // Latest initializeCybersource, so an aborted stale run can re-kick the current-JWT run without
  // capturing its own (stale) closure.
  const initializeCybersourceRef = useRef(null);
  const antifraudBeaconLoaded = useRef(false);
  const isMountedRef = useRef(true);
  // Guards against overlapping initialization runs (async SDK-readiness wait + Flex build).
  const initInFlightRef = useRef(false);
  // Active SDK-readiness poll timer, cleared on unmount.
  const sdkPollTimerRef = useRef(null);
  // One-shot timer that surfaces a non-silent failure if the capture-context JWT never arrives.
  const jwtWaitTimerRef = useRef(null);
  const binCheckedRef = useRef(false);
  // Bumped on every BIN check (and whenever inputs change); only the latest check may update state.
  const binCheckRequestIdRef = useRef(0);

  // Cancel the pending JWT-wait timeout (if any). Touches only the ref, so it's safe to call from
  // any effect without adding a dependency.
  const clearJwtWaitTimer = useCallback(() => {
    if (jwtWaitTimerRef.current) {
      clearTimeout(jwtWaitTimerRef.current);
      jwtWaitTimerRef.current = null;
    }
  }, []);

  // Prevent state updates after the component unmounts
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      if (sdkPollTimerRef.current) {
        clearInterval(sdkPollTimerRef.current);
        sdkPollTimerRef.current = null;
      }
      clearJwtWaitTimer();
    };
  }, [clearJwtWaitTimer]);

  /**
   * Find JWT token for Cybersource payment method
   * @returns {string} The JWT token for Cybersource payment method
   */
  const findJWT = useCallback(() => cybersourceJwt, [cybersourceJwt]);

  /**
   * Initialize Cybersource Flex SDK
   */
  const initializeFlex = useCallback(() => {
    const captureContext = findJWT();
    if (!captureContext) {
      // JWT not available yet (e.g. purchase still loading). Do not permanently abort — the
      // capture-context effect re-triggers initialization once the JWT arrives.
      console.error('No Cybersource JWT token found');
      return null;
    }

    // Reuse the existing Flex instance only if it was built for the current capture context.
    if (initialized.flex && currentCaptureContext.current === captureContext) {
      return flex.current;
    }

    try {
      // The script should already be loaded from initializers.js
      // eslint-disable-next-line no-undef
      flex.current = new Flex(captureContext);
      currentCaptureContext.current = captureContext;
      setInitialized((prev) => ({ ...prev, flex: true }));
      return flex.current;
    } catch (err) {
      console.error('Failed to initialize Cybersource Flex:', err);
      return null;
    }
  }, [initialized.flex, findJWT]);

  // When the capture context (JWT) changes to a new non-empty value, the current Flex instance
  // was built against a stale context (e.g. a new purchase in the same WebView session). Tear
  // down the stale Flex/microform/fields and reset the latches so the auto-init path rebuilds
  // everything against the new context. Deduped on the stored context: a stable JWT never resets
  // (guards against a re-init loop). The first build is handled by initializeFlex, so we skip
  // while no context has been recorded yet.
  useEffect(() => {
    const captureContext = findJWT();
    if (!captureContext) return;
    if (currentCaptureContext.current && currentCaptureContext.current !== captureContext) {
      fields.current = {};
      microform.current = null;
      flex.current = null;
      setInitialized((prev) => ({ ...prev, flex: false, microform: false }));
    }
  }, [findJWT]);

  // Non-silent failure when the capture-context JWT never arrives. Start a one-shot timeout while
  // the JWT is absent; clear it as soon as the JWT is present or the microform initializes. If it
  // elapses with still no JWT and no microform, surface initError (consumers relay it to the host)
  // instead of leaving a permanently blank form.
  useEffect(() => {
    if (findJWT() || initialized.microform) {
      clearJwtWaitTimer();
      return;
    }
    if (jwtWaitTimerRef.current) return;
    jwtWaitTimerRef.current = setTimeout(() => {
      jwtWaitTimerRef.current = null;
      if (isMountedRef.current && !findJWT() && !initialized.microform) {
        setInitError({ reason: 'cybersource_jwt_not_available' });
      }
    }, JWT_READY_TIMEOUT_MS);
  }, [findJWT, initialized.microform, clearJwtWaitTimer]);

  /**
   * Initialize Cybersource Microform
   */
  const initializeMicroform = useCallback(() => {
    if (!flex.current || initialized.microform) return;

    try {
      // These styles are necessary because we need to send them to the cybersource render
      const styles = {
        input: {
          'font-size': '16px',
        },
        valid: { color: '#3c763d' },
        invalid: { color: '#ff040d' },
      };

      microform.current = flex.current.microform({
        styles,
        autocomplete: 'off',
        autocorrect: 'off',
        spellcheck: 'false',
      });
      setInitialized((prev) => ({ ...prev, microform: true }));
      return microform.current;
    } catch (err) {
      console.error('Failed to initialize Cybersource Microform:', err);
      return null;
    }
  }, [initialized.microform]);

  /**
   * Set form field value
   * @param {string} field - Field name
   * @param {any} value - Field value
   */
  const setFieldValue = useCallback((field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  /**
   * Create a Microform field
   * @param {object} params - Field parameters
   * @param {string} params.fieldType - Field type (number, securityCode)
   * @param {object} params.options - Field options
   * @param {string} params.containerId - Container ID to load the field into
   * @returns {object|null} The created field or null if creation failed
   */
  const createField = useCallback(
    ({ fieldType, options, containerId }) => {
      if (!microform.current || !initialized.microform) return null;

      try {
        if (fields.current[fieldType]) {
          if (containerId && !fields.current[fieldType].loaded) {
            fields.current[fieldType].load(containerId);
          }
          return fields.current[fieldType];
        }

        const field = microform.current.createField(fieldType, {
          styles: {
            input: {
              'font-size': '16px',
            },
            valid: { color: '#3c763d' },
            invalid: { color: '#ff040d' },
          },
          autocomplete: 'off',
          autocorrect: 'off',
          ...options,
        });

        field.on('change', (data) => {
          const fieldHandlers = {
            number: () => {
              const card = getPreferredCybersourceCard(data.card);
              const brand = card?.name || '';
              const cybsCardType = card?.cybsCardType || '';

              setFieldValue('cardNumberValid', data.valid);
              if (brand) setFieldValue('cardBrand', brand);
              if (cybsCardType) setFieldValue('cybsCardType', cybsCardType);
            },
            securityCode: () => {
              setFieldValue('cardSecurityCodeValid', data.valid);
            },
          };
          fieldHandlers[fieldType]?.();
        });

        fields.current[fieldType] = field;
        if (containerId) {
          field.load(containerId);
        }

        return field;
      } catch (err) {
        console.error(`Failed to create ${fieldType} field:`, err);
        return null;
      }
    },
    [initialized.microform, setFieldValue],
  );

  /**
   * Check if Cybersource SDK script is loaded
   */
  const isCybersourceScriptLoaded = useCallback(() => {
    return typeof Flex !== 'undefined';
  }, []);

  /**
   * Wait for the global Flex SDK to become available, polling on a short interval up to a bounded
   * timeout. Resolves as soon as the SDK is defined; rejects if it never loads within the timeout.
   * @param {number} timeout - Max time to wait, in ms.
   * @param {number} pollInterval - Poll interval, in ms.
   * @returns {Promise<void>} Resolves when the SDK is ready.
   */
  const waitForFlexSdk = useCallback(
    (timeout = FLEX_SDK_READY_TIMEOUT_MS, pollInterval = FLEX_SDK_POLL_INTERVAL_MS) =>
      new Promise((resolve, reject) => {
        if (isCybersourceScriptLoaded()) {
          resolve();
          return;
        }

        const startedAt = Date.now();
        sdkPollTimerRef.current = setInterval(() => {
          if (isCybersourceScriptLoaded()) {
            clearInterval(sdkPollTimerRef.current);
            sdkPollTimerRef.current = null;
            resolve();
          } else if (Date.now() - startedAt >= timeout) {
            clearInterval(sdkPollTimerRef.current);
            sdkPollTimerRef.current = null;
            reject(new Error('Cybersource Flex SDK not available within timeout'));
          }
        }, pollInterval);
      }),
    [isCybersourceScriptLoaded],
  );

  /**
   * Load the antifraud beacon for Cybersource
   * @param {Object} options - Options for loading the beacon
   * @param {string} options.orgId - Organization ID
   * @param {string} options.merchantId - Merchant ID
   * @param {string} options.sessionId - Session ID
   * @returns {boolean} - Whether the beacon was loaded successfully
   */
  const loadAntifraudBeacon = useCallback(
    (options) => {
      if (antifraudBeaconLoaded.current) return true;

      try {
        const { orgId, sessionId, merchantId } = options;
        const url = `//h.online-metrix.net/fp/tags.js?org_id=${orgId}&session_id=${merchantId}${sessionId}`;

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = false;
        script.defer = true;
        script.src = url;

        const existingScript = document.getElementsByTagName('script')[0];
        existingScript.parentNode.insertBefore(script, existingScript);

        antifraudBeaconLoaded.current = true;
        setInitialized((prev) => ({ ...prev, antifraudBeacon: true }));
        dispatch(setCybersourceKeys(options));
        return true;
      } catch (err) {
        console.error('Failed to load Cybersource antifraud beacon:', err);
        return false;
      }
    },
    [dispatch],
  );

  /**
   * Initialize Cybersource SDK and Microform
   */
  const initializeCybersource = useCallback(async () => {
    // No capture-context JWT yet: nothing to initialize. Return WITHOUT touching isLoading so we
    // don't cause a re-render — the effect re-runs when the JWT arrives (findJWT dep changes).
    // This is the key to avoiding the render-storm while the token is absent.
    const targetJwt = findJWT();
    if (!targetJwt) return;
    // Dedupe overlapping runs so a stable context initializes exactly once (no re-init loop).
    if (initInFlightRef.current) return;
    initInFlightRef.current = true;
    setIsLoading(true);

    try {
      // Wait for the SDK script (injected without awaiting in initializers.js) to be ready.
      await waitForFlexSdk();

      // A newer capture context may have arrived while we awaited the SDK. Abandon this stale
      // attempt instead of building Flex/microform against an outdated JWT (which would commit
      // initialized.microform and permanently gate out re-init for the new context). Release the
      // in-flight lock via `finally`, then re-kick a fresh run for the current JWT.
      if (liveJwtRef.current !== targetJwt) {
        Promise.resolve().then(() => {
          initializeCybersourceRef.current?.();
        });
        return;
      }

      const flexInstance = initializeFlex();
      if (flexInstance) {
        // Build the microform, retrying a bounded number of times so a transient failure
        // (flex ready, microform not) recovers here instead of via a render loop.
        let microformInstance = initializeMicroform();
        for (
          let attempt = 1;
          !microformInstance && attempt < MICROFORM_MAX_ATTEMPTS;
          attempt += 1
        ) {
          microformInstance = initializeMicroform();
        }
      }

      // Non-silent failure for the build paths the SDK/JWT timers don't cover: Flex threw
      // (flexInstance null) or the microform never built after the bounded retries. The JWT is
      // present here, so the jwt-wait timer won't fire — surface initError explicitly instead of
      // leaving a permanently blank form. microform.current is assigned synchronously in
      // initializeMicroform, so it reliably reflects success within this call. Report which stage
      // failed so the native host gets an accurate reason (Flex vs microform) when triaging.
      if (isMountedRef.current && !microform.current) {
        setInitError({
          reason: flexInstance
            ? 'cybersource_microform_init_failed'
            : 'cybersource_flex_init_failed',
        });
      } else if (isMountedRef.current && microform.current) {
        // Recovered (e.g. JWT arrived just after the jwt-wait timeout fired, or a retry finally
        // built the microform): clear any stale failure so consumers reflect the "recoverable"
        // contract instead of showing a permanent error.
        setInitError(null);
      }
    } catch (err) {
      console.error('Error initializing Cybersource:', err);
      // SDK never became ready within the timeout (or another unrecoverable init error): surface
      // the non-silent failure signal instead of leaving a blank form.
      if (isMountedRef.current) {
        setInitError({ reason: err?.message || 'cybersource_init_failed' });
      }
    } finally {
      initInFlightRef.current = false;
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [findJWT, waitForFlexSdk, initializeFlex, initializeMicroform]);
  // Keep the ref pointing at the latest initializeCybersource so an aborted stale run re-kicks the
  // current-JWT run (not its own stale closure).
  initializeCybersourceRef.current = initializeCybersource;

  /**
   * Create payment payload with card data
   * @param {object} cardData - Card data
   * @returns {Promise<object>} Payment payload
   */
  const createPaymentPayload = useCallback(async (cardData) => {
    if (!microform.current) {
      throw new Error('Cybersource Microform not initialized');
    }

    setIsLoading(true);
    try {
      const { holderName, cardExpiration, cardBrand, cybsCardType } = cardData;

      let expirationMonth;
      let expirationYear;

      if (cardExpiration) {
        [expirationMonth, expirationYear] = parseExpirationDate(cardExpiration);
      } else {
        expirationMonth = cardData.expirationMonth;
        expirationYear = cardData.expirationYear;
      }

      const cardParams = {
        holderName,
        cardBrand: cardBrand || 'visa',
        expirationMonth,
        expirationYear,
        cybsCardType,
      };

      // Create options for token creation
      const options = {
        expirationMonth,
        expirationYear,
      };

      // Add card type code if available
      const cardTypeCode = getCardTypeCode(cardBrand);
      if (cardTypeCode) {
        options.type = cardTypeCode;
      }

      // Create token using microform
      return new Promise((resolve, reject) => {
        microform.current.createToken(options, (error, token) => {
          if (error) {
            reject(error);
            return;
          }
          resolve(mappedCyberResponse(token, cardParams));
        });
      });
    } catch (error) {
      console.error('Error creating payment payload:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Reset form values and errors
   */
  const resetForm = useCallback(() => {
    setFormValues({
      cardNumberValid: false,
      cardSecurityCodeValid: false,
      cardBrand: '',
      cybsCardType: '',
      holderName: '',
      expirationMonth: '',
      expirationYear: '',
      billingAddress: '',
    });
  }, []);

  /**
   * Check if form is valid
   * @returns {boolean} True if form is valid
   */
  const isFormValid = useCallback(() => {
    return (
      formValues.cardNumberValid &&
      formValues.cardSecurityCodeValid &&
      !!formValues.holderName &&
      !!formValues.expirationMonth &&
      !!formValues.expirationYear
    );
  }, [formValues]);

  /**
   * Check whether the entered card BIN is in the tenant's list and, if so, show the
   * digital-card recommendation message.
   *
   * Cybersource hosted fields never expose raw card digits in field events, so the BIN is
   * obtained from the transient token created here and decoded from its JWT payload.
   */
  const checkDigitalCardRecommendation = useCallback(async () => {
    if (!microform.current) return;

    binCheckRequestIdRef.current += 1;
    const requestId = binCheckRequestIdRef.current;
    // True only while this is still the most recent check and the component is mounted.
    // eslint-disable-next-line jsdoc/require-jsdoc
    const isLatest = () => isMountedRef.current && requestId === binCheckRequestIdRef.current;

    // createToken needs a real expiration (plus valid number + security-code hosted fields). This
    // token is only used to read the BIN and is discarded.
    const options = {
      expirationMonth: formValues.expirationMonth,
      expirationYear: formValues.expirationYear,
    };
    const cardTypeCode = getCardTypeCode(formValues.cardBrand);
    if (cardTypeCode) options.type = cardTypeCode;

    try {
      const token = await new Promise((resolve, reject) => {
        microform.current.createToken(options, (error, createdToken) => {
          if (error) {
            reject(error);
            return;
          }
          resolve(createdToken);
        });
      });

      const bin = getBinFromCybersourceToken(token);
      if (!bin) {
        if (isLatest()) setBinMessageVisible(false);
        return;
      }

      const response = await purchaseApi.validateInstallments({ bin }, { engine: 'cybersource' });
      if (!isLatest()) return;

      setBinMessageVisible(response.eligible ?? false);
      if (response.eligible) {
        // Don't forward the card BIN to analytics vendors; the event only needs
        // to measure that the recommendation was shown.
        trackEvent(DIGITAL_CARD_RECOMMENDATION_SHOWED, { Engine: 'cybersource' });
      }
    } catch (error) {
      console.error('Error checking Cybersource digital-card recommendation:', error);
      if (isLatest()) setBinMessageVisible(false);
    }
  }, [formValues.expirationMonth, formValues.expirationYear, formValues.cardBrand]);

  // Check the BIN once the card number, security code and expiration are valid — createToken
  // tokenizes the hosted number + security-code fields and needs a real expiration. The holder
  // name is NOT required. The BIN comes from a transient token (hosted fields never expose raw
  // digits). Gated by the flag.
  useEffect(() => {
    if (!features.SHOW_DIGITAL_CARD_RECOMMENDATION) return;

    const readyToCheck =
      formValues.cardNumberValid &&
      formValues.cardSecurityCodeValid &&
      !!formValues.expirationMonth &&
      !!formValues.expirationYear;

    if (readyToCheck && !binCheckedRef.current) {
      binCheckedRef.current = true;
      checkDigitalCardRecommendation();
    } else if (!formValues.cardNumberValid || !formValues.cardSecurityCodeValid) {
      // The number or CVV is being edited; reset so the BIN is re-checked once both are valid
      // again, and hide the message meanwhile. Bump the request id so any in-flight check is ignored.
      binCheckRequestIdRef.current += 1;
      binCheckedRef.current = false;
      setBinMessageVisible(false);
    }
  }, [
    features.SHOW_DIGITAL_CARD_RECOMMENDATION,
    formValues.cardNumberValid,
    formValues.cardSecurityCodeValid,
    formValues.expirationMonth,
    formValues.expirationYear,
    checkDigitalCardRecommendation,
  ]);

  /**
   * Initialize antifraud beacon
   */
  const initAntifraudBeacon = useCallback(() => {
    // Check if we need to use dynamic credentials
    const useDynamicCredentials = features.DYNAMIC_CYBERSOURCE_CREDENTIALS;

    if (!env?.cybersource?.enabled) return;

    // Get session ID for antifraud
    const sessionId = getSessionId();

    // Default to environment config
    let beaconOptions = {
      orgId: env.cybersource.orgId,
      merchantId: env.cybersource.merchantId,
      enabled: env.cybersource.enabled,
      sessionId,
    };

    // If using dynamic credentials and we have a purchase token
    if (useDynamicCredentials && purchaseToken && cybersourceKeys) {
      const { merchantId, orgId } = cybersourceKeys;
      const { merchantId: merchantIdLocal, orgId: orgIdLocal } = cybersourceLocalKeys || {};

      // If the keys have changed, reload the page to reinitialize
      if (
        (merchantIdLocal && merchantIdLocal !== merchantId) ||
        (orgIdLocal && orgIdLocal !== orgId)
      ) {
        window.location.reload();
        return;
      }

      // If the keys are the same, no need to reinitialize
      if (merchantIdLocal && merchantIdLocal === merchantId && orgIdLocal && orgIdLocal === orgId) {
        return;
      }

      // Use the dynamic keys if available
      if (merchantId && orgId) {
        beaconOptions = {
          merchantId,
          orgId,
          enabled: env.cybersource.enabled,
          sessionId,
        };
      }
    }

    // Load the antifraud beacon
    loadAntifraudBeacon(beaconOptions);
  }, [
    env,
    features.DYNAMIC_CYBERSOURCE_CREDENTIALS,
    purchaseToken,
    cybersourceKeys,
    cybersourceLocalKeys,
    loadAntifraudBeacon,
  ]);

  // Load antifraud beacon when cybersource keys are available
  useEffect(() => {
    initAntifraudBeacon();
  }, [initAntifraudBeacon]);

  // Expose context values. Memoized so its identity changes only when an exposed value changes —
  // otherwise every consumer re-renders on each provider render (e.g. formValues updating on every
  // keystroke), which is exactly the re-render churn this change set out to reduce (RR-4).
  const contextValue = useMemo(
    () => ({
      formValues,
      setFieldValue,
      createField,
      createPaymentPayload,
      initializeCybersource,
      resetForm,
      isFormValid,
      isLoading,
      initialized,
      binMessageVisible,
      initError,
      features: features || {},
    }),
    [
      formValues,
      setFieldValue,
      createField,
      createPaymentPayload,
      initializeCybersource,
      resetForm,
      isFormValid,
      isLoading,
      initialized,
      binMessageVisible,
      initError,
      features,
    ],
  );

  return (
    <CybersourceMicroformFormContext.Provider value={contextValue}>
      {children}
    </CybersourceMicroformFormContext.Provider>
  );
};

CybersourceMicroformFormProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
